/**
 * Featured Image Caption Component
 * Automatically displays captions for featured images based on alt text
 */
class FeaturedImageCaption {
  constructor() {
    this.initialized = false;
    this.captionSelector = '#featured-image-caption';
    this.captionTextSelector = '#featured-image-caption-text';
    this.imageContainerSelector = '#blog-post-featured-image';
  }

  init() {
    if (this.initialized) return;

    console.log('🖼️ Initializing Featured Image Caption');

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupCaptions());
    } else {
      this.setupCaptions();
    }

    this.initialized = true;
  }

  setupCaptions() {
    console.log('🖼️ Setting up featured image captions');

    // Setup featured image caption (post pages)
    this.setupFeaturedImageCaption();

    // Setup content image captions (all images in post content)
    this.setupContentImageCaptions();
  }

  setupFeaturedImageCaption() {
    const imageContainer = document.getElementById('blog-post-featured-image');

    if (!imageContainer) {
      console.log('ℹ️ No featured image container found');
      return;
    }

    console.log('🖼️ Found featured image container');

    const featuredImg = imageContainer.querySelector('img');
    const captionDiv = document.getElementById('featured-image-caption');
    const captionText = document.getElementById('featured-image-caption-text');

    console.log('🖼️ Featured image elements:', {
      image: !!featuredImg,
      captionDiv: !!captionDiv,
      captionText: !!captionText,
      altText: featuredImg ? featuredImg.alt : 'No image'
    });

    if (featuredImg && captionDiv && captionText && featuredImg.alt && featuredImg.alt.trim()) {
      console.log('✅ All conditions met for featured image caption');

      // Set caption text
      captionText.textContent = featuredImg.alt.trim();

      // Show caption with animation
      this.showCaption(captionDiv);

      console.log('🖼️ Featured image caption displayed:', featuredImg.alt.trim());
    } else {
      console.log('❌ Featured image caption conditions not met');
      console.log('- Featured image:', !!featuredImg);
      console.log('- Caption div:', !!captionDiv);
      console.log('- Caption text element:', !!captionText);
      console.log('- Alt text exists:', featuredImg && featuredImg.alt && featuredImg.alt.trim());
    }
  }

  setupContentImageCaptions() {
    const articleContent = document.getElementById('blog-post-article-content');

    if (!articleContent) {
      console.log('ℹ️ No article content found for image captions');
      return;
    }

    console.log('🖼️ Setting up content image captions');

    // Handle figure elements first
    const figures = articleContent.querySelectorAll('figure');
    figures.forEach((figure, index) => {
      // Skip if figure already has a figcaption
      if (figure.querySelector('figcaption')) {
        console.log(`ℹ️ Figure ${index + 1} already has figcaption, skipping`);
        return;
      }

      // Find image with alt text
      const img = figure.querySelector('img[alt]');
      if (img && img.alt.trim()) {
        console.log(`🖼️ Creating auto-caption for figure ${index + 1}:`, img.alt.trim());

        // Make figure container relative if not already
        if (getComputedStyle(figure).position === 'static') {
          figure.style.position = 'relative';
        }

        // Create auto-caption element
        const autoCaption = document.createElement('div');
        autoCaption.className = 'auto-caption';
        autoCaption.textContent = img.alt.trim();

        // Insert caption into figure
        figure.appendChild(autoCaption);
      }
    });

    // Handle standalone img tags (Ghost CMS default output)
    const standaloneImages = articleContent.querySelectorAll('img[alt]:not(figure img)');
    standaloneImages.forEach((img, index) => {
      if (img.alt.trim() && !img.nextElementSibling?.classList.contains('auto-caption')) {
        console.log(`🖼️ Creating wrapper and caption for standalone image ${index + 1}:`, img.alt.trim());

        // Create a wrapper div for the image
        const wrapper = document.createElement('div');
        wrapper.className = 'image-wrapper';
        wrapper.style.position = 'relative';
        wrapper.style.display = 'inline-block';
        wrapper.style.width = '100%';

        // Insert wrapper before image and move image into wrapper
        img.parentNode.insertBefore(wrapper, img);
        wrapper.appendChild(img);

        // Create auto-caption element
        const autoCaption = document.createElement('div');
        autoCaption.className = 'auto-caption';
        autoCaption.textContent = img.alt.trim();

        // Insert caption into wrapper
        wrapper.appendChild(autoCaption);
      }
    });
  }

  showCaption(captionDiv) {
    // Show caption with smooth animation
    captionDiv.style.display = 'block';
    captionDiv.style.opacity = '0';
    captionDiv.style.transform = 'translateY(-10px)';

    // Animate in
    requestAnimationFrame(() => {
      captionDiv.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      captionDiv.style.opacity = '1';
      captionDiv.style.transform = 'translateY(0)';
    });
  }

  hideCaption(captionDiv) {
    // Hide caption with smooth animation
    captionDiv.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    captionDiv.style.opacity = '0';
    captionDiv.style.transform = 'translateY(-10px)';

    setTimeout(() => {
      captionDiv.style.display = 'none';
    }, 300);
  }

  // Method to manually trigger caption setup (for dynamic content)
  refresh() {
    console.log('🖼️ Refreshing featured image captions');
    this.setupCaptions();
  }
}

export default FeaturedImageCaption;